import {
    Box,
    Button,
    Chip,
    FormControl,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    Car,
    Download,
    Eye,
    Factory,
    Pencil,
    Search,
    Trash2,
    X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import MobileCrudList from '../../component/common/MobileCrud';
import { KitChip, KitChips, KitField, KitPlate, KitSelect } from '../../component/common/MobileKitExtras';
import { protoIcon } from '../../component/common/ProtoIcon';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import FilterToolbar, { FilterChips } from '../../component/filter/FilterToolbar';
import DialogForm from '../../component/dialog/DialogForm';
import NewItemButton from '../../component/common/NewItemButton';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import usePermissions from '../../hooks/usePermissions';
import ManufacturersModal from './ManufacturersModal';
import VehicleViewDrawer from './View';

const PAGE_SIZE_OPTIONS = [5, 8, 15];
const MOBILE_PAGE_SIZE = 20;

const CarIcon = protoIcon('car');
const EyeIcon = protoIcon('eye');
const EditIcon = protoIcon('edit');
const TrashIcon = protoIcon('trash');
const SettingsIcon = protoIcon('settings');
const DownloadIcon = protoIcon('download');

function fmtKm(value) {
    const n = Number(value) || 0;
    return `${n.toLocaleString('pt-BR')} km`;
}

function fmtYear(row) {
    if (row.FabYear && row.ModYear && row.FabYear !== row.ModYear) {
        return `${row.FabYear}/${row.ModYear}`;
    }
    return row.ModYear || row.FabYear || '—';
}

// Filtro aplicado só ao clicar "Buscar"/Enter — mesma técnica de
// `suppliers/List.js`/`customers/List.js` (report-toolbar §6.1): o estado do
// campo é distinto do filtro efetivamente aplicado, que alimenta os chips
// removíveis e a busca no servidor.
export default function VehiclesList() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const isMobileShell = useIsMobileShell();
    // Mobile: lista contínua ("Carregar mais") em vez de paginação.
    const [mobilePage, setMobilePage] = useState(0);
    // Mobile: chips de montadora (a busca do servidor já cobre o fabricante) — acumula as vistas.
    const [brandChips, setBrandChips] = useState([]);

    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    // Paginação server-side: `page` é 0-based (DefaultTable); a API usa 1-based.
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(8);
    const [totalCount, setTotalCount] = useState(0);
    const [reloadKey, setReloadKey] = useState(0);
    // Opções do filtro "Cliente" ({ id, name }).
    const [clienteOptions, setClienteOptions] = useState([]);

    // Valores dos campos do formulário de filtro (não aplicados ainda).
    // `clienteFilter`/`appliedCliente` guardam o id do cliente.
    const [clienteFilter, setClienteFilter] = useState('');
    const [search, setSearch] = useState('');
    // Filtros efetivamente aplicados — alimentam os chips removíveis.
    const [appliedCliente, setAppliedCliente] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');

    const [viewVehicleId, setViewVehicleId] = useState(null);
    const [openViewDrawer, setOpenViewDrawer] = useState(false);
    const [openDialogConfirmDelete, setOpenDialogConfirmDelete] = useState(false);
    const [vehicleToDelete, setVehicleToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [openManufacturersModal, setOpenManufacturersModal] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // Busca a página atual no servidor sempre que filtros aplicados/página/tamanho mudam.
    useEffect(() => {
        let cancelled = false;
        const requestPage = isMobileShell ? mobilePage : page;
        const params = { page: requestPage + 1, page_size: isMobileShell ? MOBILE_PAGE_SIZE : pageSize };
        if (appliedCliente) params.customerId = appliedCliente;
        if (appliedSearch.trim()) params.search = appliedSearch.trim();

        setLoading(true);
        httpClient.get('/vehicles', { params })
            .then((response) => {
                if (cancelled) return;
                const rows = response.data?.items || [];
                const total = response.data?.total || 0;
                // Página ficou além do fim (ex.: excluiu o último item da última página).
                if (!isMobileShell && rows.length === 0 && total > 0 && page > 0) {
                    setPage(Math.ceil(total / pageSize) - 1);
                    return;
                }
                if (isMobileShell && requestPage > 0) {
                    setVehicles((prev) => [...prev, ...rows.filter((row) => !prev.some((p) => p.ID === row.ID))]);
                } else {
                    setVehicles(rows);
                }
                setTotalCount(total);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Error fetching vehicles:', error);
                setVehicles([]);
                setTotalCount(0);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [page, pageSize, appliedCliente, appliedSearch, reloadKey, isMobileShell, mobilePage]);

    useEffect(() => {
        if (!isMobileShell) return;
        const seen = vehicles.map((row) => row.Manufacturer).filter(Boolean);
        if (seen.length === 0) return;
        setBrandChips((prev) => {
            const next = [...prev];
            seen.forEach((name) => { if (!next.includes(name)) next.push(name); });
            return next.length === prev.length ? prev : next.slice(0, 8);
        });
    }, [vehicles, isMobileShell]);

    const handleMobileBrand = (brand) => {
        setSearch(brand);
        setAppliedSearch(brand);
        setMobilePage(0);
    };

    // Mobile: a busca é aplicada sozinha (debounce), sem botão "Buscar".
    useEffect(() => {
        if (!isMobileShell || search.trim() === appliedSearch) return undefined;
        const timer = setTimeout(() => {
            setAppliedSearch(search.trim());
            setMobilePage(0);
        }, 400);
        return () => clearTimeout(timer);
    }, [search, appliedSearch, isMobileShell]);

    // Opções do filtro "Cliente": a listagem paginada não traz todos os clientes,
    // então usa a lista de clientes (inclui clientes sem veículo).
    useEffect(() => {
        let cancelled = false;
        httpClient.get('/customers')
            .then((response) => {
                if (cancelled) return;
                const list = Array.isArray(response.data) ? response.data : [];
                setClienteOptions(
                    list
                        .filter((c) => c.ID && c.Name)
                        .map((c) => ({ id: c.ID, name: c.Name }))
                        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
                );
            })
            .catch((error) => {
                if (!cancelled) console.error('Error fetching customers:', error);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const appliedClienteName = clienteOptions.find((c) => c.id === appliedCliente)?.name || '';

    const handleApplyFilters = () => {
        setAppliedCliente(clienteFilter);
        setAppliedSearch(search);
        setPage(0);
        setMobilePage(0);
    };

    const handleClearFilters = () => {
        setClienteFilter('');
        setSearch('');
        setAppliedCliente('');
        setAppliedSearch('');
        setPage(0);
        setMobilePage(0);
    };

    const handleRemoveClienteFilter = () => {
        setClienteFilter('');
        setAppliedCliente('');
        setPage(0);
    };

    const handleRemoveSearchFilter = () => {
        setSearch('');
        setAppliedSearch('');
        setPage(0);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPage(0);
    };

    const handleViewVehicle = (row) => {
        setViewVehicleId(row.ID);
        setOpenViewDrawer(true);
    };

    const handleDeleteVehicle = (row) => {
        setVehicleToDelete(row);
        setOpenDialogConfirmDelete(true);
    };

    const confirmDelete = async () => {
        try {
            setDeleting(true);
            await httpClient.delete(`/vehicles/${vehicleToDelete.ID}`);
            setOpenDialogConfirmDelete(false);
            setVehicleToDelete(null);
            setReloadKey((k) => k + 1);
            setSnackbarMessage('Veículo excluído com sucesso!');
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
        } catch (error) {
            console.error('Error deleting vehicle:', error);
            setSnackbarMessage('Erro ao excluir veículo.');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setDeleting(false);
        }
    };

    const handleExport = async () => {
        try {
            await httpClient.downloadFile('/vehicles/export', {
                title: 'veiculos',
                extension: 'csv',
                responseType: 'blob'
            });
        } catch (error) {
            console.error('Error exporting vehicles:', error);
            setSnackbarMessage('Erro ao exportar veículos.');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        }
    };

    const columns = [
        {
            path: 'LicensePlate',
            name: 'Placa',
            invisible: false,
            render: (row) => (
                row.LicensePlate ? (
                    <Chip label={row.LicensePlate} size="small" sx={{ bgcolor: 'info.bg', color: 'primary.main', fontWeight: 700 }} />
                ) : (
                    <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>—</Typography>
                )
            ),
        },
        {
            path: 'Model',
            name: 'Modelo',
            invisible: false,
            render: (row) => (
                <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>{row.Model || '—'}</Typography>
                    <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>{row.Manufacturer || ''}</Typography>
                </Box>
            ),
        },
        {
            path: 'ModYear',
            name: 'Ano',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{fmtYear(row)}</Typography>
            ),
        },
        {
            path: 'Color',
            name: 'Cor',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: row.Color ? 'text.secondary' : 'text.disabled' }}>{row.Color || '—'}</Typography>
            ),
        },
        {
            path: 'Customer',
            name: 'Cliente',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{row.Customer || '—'}</Typography>
            ),
        },
        {
            path: 'Kilometer',
            name: 'Km',
            invisible: false,
            render: (row) => (
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{fmtKm(row.Kilometer)}</Typography>
            ),
        },
    ];

    const actions = [
        ...(hasPermission('vehicle.view') ? [{
            icon: <Eye size={16} strokeWidth={2} />,
            props: { onClick: handleViewVehicle, tooltip: 'Visualizar', style: { cursor: 'pointer' } }
        }] : []),
        ...(hasPermission('vehicle.update') ? [{
            icon: <Pencil size={16} strokeWidth={2} />,
            props: { onClick: (row) => navigate(`/vehicles/edit/${row.ID}`), tooltip: 'Editar', style: { cursor: 'pointer' } }
        }] : []),
        ...(hasPermission('vehicle.delete') ? [{
            icon: <Trash2 size={16} strokeWidth={2} color={theme.palette.error.main} />,
            props: { onClick: handleDeleteVehicle, tooltip: 'Excluir', style: { cursor: 'pointer' } }
        }] : []),
    ];

    const sharedDialogs = (
        <>
            <VehicleViewDrawer
                open={openViewDrawer}
                vehicleId={viewVehicleId}
                onClose={() => setOpenViewDrawer(false)}
            />

            <DialogForm
                iconType="warning"
                open={openDialogConfirmDelete}
                title="Atenção"
                confirmButtonText="Sim, Excluir"
                cancelButtonText="Cancelar"
                content={`Tem certeza que deseja excluir o veículo "${vehicleToDelete?.LicensePlate || vehicleToDelete?.Model || ''}"?`}
                secondaryContent="Esta ação não pode ser desfeita. O veículo será removido permanentemente do sistema."
                onSubmit={confirmDelete}
                onClose={() => {
                    setOpenDialogConfirmDelete(false);
                    setVehicleToDelete(null);
                }}
                loading={deleting}
                loadingText="Removendo..."
            />

            <ManufacturersModal
                open={openManufacturersModal}
                onClose={() => setOpenManufacturersModal(false)}
            />
        </>
    );

    // Shell mobile: lista do protótipo `veiculos/lista.html` (mesmos dados, filtros e ações).
    if (isMobileShell) {
        const mobileMoreItems = [
            { label: 'Montadoras', icon: SettingsIcon, onClick: () => setOpenManufacturersModal(true) },
            ...(hasPermission('vehicle.export') ? [{ label: 'Exportar', icon: DownloadIcon, onClick: handleExport }] : []),
        ];
        return (
            <PageScaffold
                links={[]}
                allowBleed
                titlePageTarget="Veículos"
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
            >
                <MobileCrudList
                    items={vehicles}
                    getKey={(row) => row.ID}
                    getRow={(row) => ({
                        icon: CarIcon,
                        title: [row.Manufacturer, row.Model].filter(Boolean).join(' ') || '—',
                        sub: [row.Customer, fmtYear(row) === '—' ? '' : fmtYear(row)].filter(Boolean).join(' · '),
                        end: row.LicensePlate ? <KitPlate>{row.LicensePlate}</KitPlate> : undefined,
                    })}
                    loading={loading}
                    search={search}
                    onSearchChange={setSearch}
                    searchPlaceholder="Placa, modelo ou cliente…"
                    topSlot={(
                        <KitChips>
                            <KitChip active={!appliedSearch} count={!appliedSearch && !loading ? totalCount : undefined} onClick={() => handleMobileBrand('')}>Todos</KitChip>
                            {brandChips.map((brand) => (
                                <KitChip
                                    key={brand}
                                    active={appliedSearch.toLowerCase() === brand.toLowerCase()}
                                    onClick={() => handleMobileBrand(brand)}
                                >
                                    {brand}
                                </KitChip>
                            ))}
                        </KitChips>
                    )}
                    filter={{
                        active: Boolean(appliedCliente),
                        onOpen: () => setClienteFilter(appliedCliente),
                        onApply: handleApplyFilters,
                        onClear: handleClearFilters,
                        children: (
                            <KitField label="Cliente">
                                <KitSelect value={clienteFilter} onChange={(e) => setClienteFilter(e.target.value)} aria-label="Cliente">
                                    <option value="">Todos</option>
                                    {clienteOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </KitSelect>
                            </KitField>
                        ),
                    }}
                    emptyIcon={CarIcon}
                    emptyTitle="Nenhum veículo encontrado"
                    emptyText="Ajuste a busca ou os filtros para ver outros veículos."
                    hasMore={vehicles.length < totalCount}
                    onLoadMore={() => setMobilePage((current) => current + 1)}
                    fabLabel={hasPermission('vehicle.create') ? 'Novo' : undefined}
                    onNew={() => navigate('/vehicles/new')}
                    moreItems={mobileMoreItems}
                    rowTitle={(row) => [row.Manufacturer, row.Model].filter(Boolean).join(' ') || row.LicensePlate || 'Veículo'}
                    rowActions={(row) => [
                        ...(hasPermission('vehicle.view') ? [{ label: 'Visualizar', icon: EyeIcon, onClick: () => handleViewVehicle(row) }] : []),
                        ...(hasPermission('vehicle.update') ? [{ label: 'Editar', icon: EditIcon, onClick: () => navigate(`/vehicles/edit/${row.ID}`) }] : []),
                        ...(hasPermission('vehicle.delete') ? [{ label: 'Excluir', icon: TrashIcon, destructive: true, onClick: () => handleDeleteVehicle(row) }] : []),
                    ]}
                />
                {sharedDialogs}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            links={[]}
            titlePageTarget="Veículos"
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={snackbarMessage}
            severity={snackbarSeverity}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — ícone-badge + título + descrição + ações. */}
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <Car size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Veículos
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Cadastro de veículos vinculados aos clientes.
                            </Typography>
                        </Box>
                    </Box>

                    <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
                        {hasPermission('vehicle.export') && (
                            <Button variant="outlined" startIcon={<Download size={15} strokeWidth={2} />} onClick={handleExport}>
                                Exportar
                            </Button>
                        )}
                        <Button variant="outlined" startIcon={<Factory size={15} strokeWidth={2} />} onClick={() => setOpenManufacturersModal(true)}>
                            Fabricantes
                        </Button>
                        {hasPermission('vehicle.create') && (
                            <NewItemButton label="Novo veículo" onClick={() => navigate('/vehicles/new')} />
                        )}
                    </Stack>
                </Box>

                {/* report-toolbar — Cliente + Buscar, em card separado. */}
                <FilterToolbar
                    plain
                    activeCount={clienteFilter ? 1 : 0}
                    onClear={handleClearFilters}
                    onApply={handleApplyFilters}
                    primary={(
                        <Box sx={{ flex: '0 0 300px' }}>
                            <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
                                Buscar
                            </Typography>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Placa, modelo ou cliente…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={15} strokeWidth={2} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: 40, fontSize: '13px', borderRadius: '9px' },
                                }}
                            />
                        </Box>
                    )}
                    actions={(
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Button
                                variant="text"
                                onClick={handleClearFilters}
                                sx={{ height: 40, px: 1.25, color: 'text.secondary', fontSize: '12.5px', fontWeight: 600, '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                            >
                                Limpar
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Search size={15} strokeWidth={2} />}
                                onClick={handleApplyFilters}
                                sx={{ height: 40, px: 2.25 }}
                            >
                                Buscar
                            </Button>
                        </Stack>
                    )}
                >
                        <Box data-wide>
                            <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
                                Cliente
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 220 }, maxWidth: '100%' }}>
                                <Select
                                    value={clienteFilter}
                                    displayEmpty
                                    onChange={(e) => setClienteFilter(e.target.value)}
                                    renderValue={(value) => clienteOptions.find((c) => c.id === value)?.name || 'Todos'}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {clienteOptions.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                </FilterToolbar>

                {/* chips-row — filtros ativos removíveis + contagem. */}
                <FilterChips>
                    {appliedCliente && (
                        <Chip
                            label={`Cliente: ${appliedClienteName || '...'}`}
                            size="small"
                            onDelete={handleRemoveClienteFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                        />
                    )}
                    {appliedSearch && (
                        <Chip
                            label={`Busca: "${appliedSearch}"`}
                            size="small"
                            onDelete={handleRemoveSearchFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    <Chip
                        label={`${totalCount} veículo${totalCount === 1 ? '' : 's'} encontrado${totalCount === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'success.bg', color: 'success.main' }}
                    />
                </FilterChips>

                <DefaultTable
                    colunms={columns}
                    data={vehicles}
                    iconsActions={actions}
                    loading={loading}
                    onRowClick={hasPermission('vehicle.view') ? handleViewVehicle : undefined}
                    rowPerPage={pageSize}
                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    itemLabel="veículos"
                    serverPagination={{
                        page,
                        rowsPerPage: pageSize,
                        total: totalCount,
                        onPageChange: setPage,
                        onRowsPerPageChange: handlePageSizeChange,
                    }}
                />
            </Box>

            {sharedDialogs}
        </PageScaffold>
    );
}
